/**
 * LinkedIn API Integration for KzeroDiary Marketing
 *
 * OAuth 2.0 flow + Share on LinkedIn API
 */

const CLIENT_ID = process.env.REACT_APP_LINKEDIN_CLIENT_ID || '77hq7udi8itx5q';
const REDIRECT_URI = window.location.origin + '/callback/linkedin';
const SCOPES = 'openid profile w_member_social';

// ═══ OAuth ═══

/** Inicia o fluxo OAuth — redireciona para o LinkedIn */
export function linkedinLogin() {
  const state = Math.random().toString(36).substring(2, 15);
  localStorage.setItem('linkedin_oauth_state', state);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    state,
    scope: SCOPES,
  });

  window.location.href = `https://www.linkedin.com/oauth/v2/authorization?${params}`;
}

/** Troca o code por access token (via proxy CORS — Supabase ou worker) */
export async function linkedinExchangeCode(code) {
  // LinkedIn não permite trocar token client-side (CORS blocked)
  // Usamos um proxy simples via Supabase Edge Function ou allorigins
  const CLIENT_SECRET = process.env.REACT_APP_LINKEDIN_CLIENT_SECRET || '';

  // Tentar via proxy CORS público para desenvolvimento
  const tokenUrl = 'https://www.linkedin.com/oauth/v2/accessToken';
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    code,
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    redirect_uri: REDIRECT_URI,
  });

  try {
    const res = await fetch(tokenUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error_description || `HTTP ${res.status}`);
    }

    const data = await res.json();

    // Salvar token
    localStorage.setItem('linkedin_access_token', data.access_token);
    localStorage.setItem('linkedin_token_expires', String(Date.now() + (data.expires_in * 1000)));
    if (data.id_token) localStorage.setItem('linkedin_id_token', data.id_token);

    // Buscar perfil do usuário
    await fetchLinkedinProfile(data.access_token);

    return data;
  } catch (err) {
    console.error('LinkedIn token exchange error:', err);
    throw err;
  }
}

/** Busca o perfil do usuário autenticado */
async function fetchLinkedinProfile(token) {
  try {
    const res = await fetch('https://api.linkedin.com/v2/userinfo', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const profile = await res.json();
      localStorage.setItem('linkedin_profile', JSON.stringify({
        sub: profile.sub,
        name: profile.name,
        email: profile.email,
        picture: profile.picture,
      }));
      return profile;
    }
  } catch (err) {
    console.error('LinkedIn profile fetch error:', err);
  }
}

// ═══ Status ═══

/** Verifica se está conectado ao LinkedIn */
export function isLinkedinConnected() {
  const token = localStorage.getItem('linkedin_access_token');
  const expires = parseInt(localStorage.getItem('linkedin_token_expires') || '0');
  return token && Date.now() < expires;
}

/** Retorna o perfil LinkedIn salvo */
export function getLinkedinProfile() {
  try {
    return JSON.parse(localStorage.getItem('linkedin_profile') || 'null');
  } catch { return null; }
}

/** Desconecta do LinkedIn */
export function linkedinLogout() {
  localStorage.removeItem('linkedin_access_token');
  localStorage.removeItem('linkedin_token_expires');
  localStorage.removeItem('linkedin_id_token');
  localStorage.removeItem('linkedin_profile');
  localStorage.removeItem('linkedin_oauth_state');
}

// ═══ Publicar ═══

/** Publica um post de texto no LinkedIn (perfil pessoal) */
export async function linkedinPublishPost(text, imageUrl = null) {
  const token = localStorage.getItem('linkedin_access_token');
  if (!token) throw new Error('Não conectado ao LinkedIn. Faça login primeiro.');

  const profile = getLinkedinProfile();
  if (!profile?.sub) throw new Error('Perfil LinkedIn não encontrado. Reconecte.');

  const authorUrn = `urn:li:person:${profile.sub}`;

  const payload = {
    author: authorUrn,
    lifecycleState: 'PUBLISHED',
    specificContent: {
      'com.linkedin.ugc.ShareContent': {
        shareCommentary: { text },
        shareMediaCategory: imageUrl ? 'ARTICLE' : 'NONE',
        ...(imageUrl ? {
          media: [{
            status: 'READY',
            originalUrl: imageUrl,
          }]
        } : {}),
      }
    },
    visibility: {
      'com.linkedin.ugc.MemberNetworkVisibility': 'PUBLIC'
    }
  };

  const res = await fetch('https://api.linkedin.com/v2/ugcPosts', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-Restli-Protocol-Version': '2.0.0',
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message || `LinkedIn API error: ${res.status}`);
  }

  return await res.json();
}
